import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  debounceTime,
  filter,
  lastValueFrom,
  tap,
} from 'rxjs';

/**
 * Debounces the given observable for the given period of time, and calls the callback function with the latest value.
 * If the callback returns an observable, then it will not call it again until it has completed.
 */
export function debounceWait<T>(
  source$: Observable<T>,
  dueTime: number,
  handle: (value: T) => (never | Observable<any> | Promise<any>),
): Subscription {
  const isBlocked$ = new BehaviorSubject(false);
  let hasAlreadyHandledLatest = false;

  return combineLatest([
    source$.pipe(
      debounceTime(dueTime),
      tap(() => hasAlreadyHandledLatest = false), // prevent handling the same value twice
    ),
    isBlocked$,
  ])
    .pipe(filter(([, isBlocked]) => !isBlocked))
    .subscribe(async ([value]) => {
      if (hasAlreadyHandledLatest) {
        return;
      }
      hasAlreadyHandledLatest = true;
      isBlocked$.next(true);
      const cbResult = handle(value);
      if (cbResult instanceof Promise) {
        await cbResult;
      } else if (cbResult instanceof Observable) {
        await lastValueFrom(cbResult);
      }
      isBlocked$.next(false);
    });
}
